/**
 * The collector fleet by machine: the overview figures, the bands of the list,
 * pages of machines or flat pairings, one machine's panel, and the export.
 * Every read is served from the server's shared fleet snapshot, so a page of a
 * fleet of thousands is a filter and a sort, never a scan.
 */

import type {
  CollectorMachineDetail,
  MachineFilterQuery,
  MachineFleetOverview,
  MachineGroups,
  MachineGroupsQuery,
  MachineListQuery,
  MachinePage,
  PairingListQuery,
  PairingPage,
} from '@/types/collectorMachines'
import { apiClient } from './client'
import { repeatedKeyParamsSerializer } from './queryParams'

const BASE = '/api/v1/collectors'

export const collectorMachines = {
  overview: async (): Promise<MachineFleetOverview> => {
    const response = await apiClient.get<MachineFleetOverview>(
      `${BASE}/machines/overview/`
    )
    return response.data
  },

  groups: async (params: MachineGroupsQuery): Promise<MachineGroups> => {
    const response = await apiClient.get<MachineGroups>(
      `${BASE}/machines/groups/`,
      { params }
    )
    return response.data
  },

  // `ids` travels as repeated keys (?ids=a&ids=b), the form the server reads.
  list: async (params: MachineListQuery): Promise<MachinePage> => {
    const response = await apiClient.get<MachinePage>(`${BASE}/machines/`, {
      params,
      paramsSerializer: repeatedKeyParamsSerializer,
    })
    return response.data
  },

  detail: async (installationId: string): Promise<CollectorMachineDetail> => {
    const response = await apiClient.get<CollectorMachineDetail>(
      `${BASE}/machines/${encodeURIComponent(installationId)}/`
    )
    return response.data
  },

  pairings: async (params: PairingListQuery): Promise<PairingPage> => {
    const response = await apiClient.get<PairingPage>(`${BASE}/pairings/`, {
      params,
    })
    return response.data
  },

  exportCsv: async (params: MachineFilterQuery): Promise<Blob> => {
    const response = await apiClient.get<Blob>(`${BASE}/pairings/export/`, {
      params,
      responseType: 'blob',
    })
    return response.data
  },
}
